
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Search, Filter, Package, Building2, Printer, BookOpen, Shield } from 'lucide-react';

interface FilamentManagementSidebarProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  selectedManufacturer: string;
  setSelectedManufacturer: (manufacturer: string) => void;
  selectedType: string;
  setSelectedType: (type: string) => void;
  selectedStatus: string;
  setSelectedStatus: (status: string) => void;
  activeSection: 'filaments' | 'manufacturers' | 'printers' | 'tutorials' | 'page-access';
  setActiveSection: (section: 'filaments' | 'manufacturers' | 'printers' | 'tutorials' | 'page-access') => void;
}

const FilamentManagementSidebar = ({
  searchQuery,
  setSearchQuery,
  selectedManufacturer,
  setSelectedManufacturer,
  selectedType,
  setSelectedType,
  selectedStatus,
  setSelectedStatus,
  activeSection,
  setActiveSection
}: FilamentManagementSidebarProps) => {
  const sections = [
    { id: 'filaments' as const, label: 'Filaments', icon: Package },
    { id: 'manufacturers' as const, label: 'Manufacturers', icon: Building2 },
    { id: 'printers' as const, label: 'Printers', icon: Printer },
    { id: 'tutorials' as const, label: 'Tutorials', icon: BookOpen },
    { id: 'page-access' as const, label: 'Page Access', icon: Shield }
  ];

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedManufacturer('all');
    setSelectedType('all');
    setSelectedStatus('all');
  };

  return (
    <div className="h-full bg-card border-r border-border p-4 space-y-6">
      {/* Section Navigation */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Management Sections</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {sections.map((section) => {
            const Icon = section.icon;
            return (
              <Button
                key={section.id}
                variant={activeSection === section.id ? "default" : "ghost"}
                className="w-full justify-start"
                onClick={() => setActiveSection(section.id)}
              >
                <Icon className="mr-2 h-4 w-4" />
                {section.label}
              </Button>
            );
          })}
        </CardContent>
      </Card>

      {/* Search - Only show for sections that need it */}
      {activeSection !== 'page-access' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center">
              <Search className="mr-2 h-4 w-4" />
              Search
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="search">Search {activeSection}</Label>
              <Input
                id="search"
                placeholder={`Search ${activeSection}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters - Only show for sections that need them */}
      {activeSection !== 'page-access' && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center">
              <Filter className="mr-2 h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Status Filter - Available for all sections except page-access */}
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger id="status">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  {activeSection === 'tutorials' && <SelectItem value="draft">Draft</SelectItem>}
                </SelectContent>
              </Select>
            </div>

            {/* Section-specific filters */}
            {activeSection === 'filaments' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Select value={selectedManufacturer} onValueChange={setSelectedManufacturer}>
                    <SelectTrigger id="manufacturer">
                      <SelectValue placeholder="All manufacturers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Manufacturers</SelectItem>
                      {/* Add manufacturer options dynamically */}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type">Filament Type</Label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger id="type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="PLA">PLA</SelectItem>
                      <SelectItem value="ABS">ABS</SelectItem>
                      <SelectItem value="PETG">PETG</SelectItem>
                      <SelectItem value="TPU">TPU</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {activeSection === 'tutorials' && (
              <div className="space-y-2">
                <Label htmlFor="difficulty">Difficulty</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger id="difficulty">
                    <SelectValue placeholder="All difficulties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Difficulties</SelectItem>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button variant="outline" size="sm" onClick={clearFilters} className="w-full">
              Clear Filters
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Active Filters - Only show for sections that use filters */}
      {activeSection !== 'page-access' && (searchQuery || selectedManufacturer !== 'all' || selectedType !== 'all' || selectedStatus !== 'all') && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Active Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {searchQuery && (
                <Badge variant="secondary" className="text-xs">
                  Search: {searchQuery}
                </Badge>
              )}
              {selectedManufacturer !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  Manufacturer: {selectedManufacturer}
                </Badge>
              )}
              {selectedType !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  {activeSection === 'tutorials' ? 'Difficulty' : 'Type'}: {selectedType}
                </Badge>
              )}
              {selectedStatus !== 'all' && (
                <Badge variant="secondary" className="text-xs">
                  Status: {selectedStatus}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default FilamentManagementSidebar;
